<template>
  <app-dialog
    v-model="dialog"
    :value.sync="dialog"
    content-class="sourcery__dialog"
    width="100%">
    <template #activator="{ on, attrs }">
      <v-tooltip bottom>
        <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
          <v-icon
            class="pointer"
            color="black"
            v-bind="{ ...attrs, ...tooltipAttrs }"
            v-on="{ ...on, ...tooltipOn }">
            mdi-history
          </v-icon>
        </template>
        <span>Edit History</span>
      </v-tooltip>
    </template>

    <!-- Dialog Content -->
    <v-card>
      <v-card-title>
        <div class="d-flex gap-2 align-center">
          <span>History</span>

          <v-chip
            v-if="showOnlyForRowId"
            class="ml-2"
            color="primary"
            outlined
            small>
            <v-icon
              left
              small>
              mdi-filter-variant
            </v-icon>
            {{ currRowType }}
          </v-chip>

          <v-icon
            class="ml-12"
            color="grey darken-2">
            mdi-filter
          </v-icon>

          <v-select
            v-if="!showOnlyForRowId"
            v-model="selectedType"
            :items="typeOptions"
            label="Filter by Type"
            clearable
            dense
            outlined
            hide-details
            style="min-width: 150px;" />

          <v-select
            v-model="selectedUser"
            :items="userOptions"
            label="Filter by User"
            clearable
            dense
            outlined
            hide-details
            style="min-width: 150px;" />

          <v-btn
            v-if="hasActiveFilters"
            color="grey"
            text
            small
            @click="clearAllFilters">
            Clear All
          </v-btn>
        </div>

        <span
          class="sourcery__icon-wrapper black"
          @click="dialog = false">
          <v-icon
            color="white"
            size="15">
            mdi-close
          </v-icon>
        </span>
      </v-card-title>

      <v-card-text>
        <div class="edit-history-wrapper">
          <div class="edit-history-row fs-14 font-weight-bold sticky">
            <div>
              Type
            </div>
            <div class="edit-history-row__username">
              User
            </div>
            <div class="edit-history-row__action">
              Action
            </div>
            <div class="edit-history_row__column">
              Field
            </div>
            <div class="edit-history-row__value">
              From
            </div>
            <div class="edit-history-row__value">
              To
            </div>
            <div class="d-flex edit-history-row__timestamp">
              Time
            </div>
          </div>
          <v-list
            v-if="filteredEditHistory.length"
            style="max-height:100%; overflow-y: auto;">
            <v-list-item
              v-for="(action, index) in filteredEditHistory"
              :key="index"
              class="edit-history-row">
              <div class="edit-history-row__type">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on">
                      {{ getTruncatedType(action) }}
                    </span>
                  </template>
                  <span>{{ action.rowType || '' }}</span>
                </v-tooltip>
              </div>
              <div class="edit-history-row__username">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on">
                      {{ getTruncatedUsername(action) }}
                    </span>
                  </template>
                  <span>{{ action.creatorName || 'Someone' }}</span>
                </v-tooltip>
              </div>
              <div class="edit-history-row__action">
                {{ getActionCommand(action) }}
              </div>
              <div class="edit-history_row__column">
                {{ getActionField(action) }}
              </div>
              <div class="edit-history-row__value">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on">
                      {{ getTruncatedActionVal(action, true) }}
                    </span>
                  </template>
                  <span>{{ formatActionVal(action, true) }}</span>
                </v-tooltip>
              </div>
              <div class="edit-history-row__value">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on">
                      {{ getTruncatedActionVal(action, false) }}
                    </span>
                  </template>
                  <span>{{ formatActionVal(action, false) }}</span>
                </v-tooltip>
              </div>
              <div class="d-flex edit-history-row__timestamp">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <span
                      class="fs-12 text-grey-lighten-1"
                      v-bind="attrs"
                      v-on="on">
                      {{ formatRelativeTimestamp(action.createdTime) }}
                    </span>
                  </template>
                  <span>{{ formatExactTimestamp(action.createdTime) }}</span>
                </v-tooltip>
              </div>
            </v-list-item>
          </v-list>
          <div
            v-else-if="scheduleCommands.length"
            class="edit-history-row text-center py-4">
            <div
              class="text-grey"
              style="grid-column: 1 / -1;">
              {{ `No entries ${showOnlyForRowId ? '' : 'match the current filters'}` }}
            </div>
          </div>
          <div
            v-else
            class="edit-history-row">
            <div
              v-for="n in 7"
              :key="n"
              class="pa-3 flex-grow-1 mx-0">
              -
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
    <v-btn
      class="white sourcery__dialog-close"
      fab
      large
      @click="dialog = false">
      <img src="@/assets/icons/chevron-right-icon.svg">
    </v-btn>
  </app-dialog>
</template>

<script>
import {
  mapState,
  mapActions,
} from 'vuex';
import { subscribeForScheduleCommands } from '@/services/graphql/scheduleRowsSubscriptions';
import ScheduleAPI from '@/services/graphql/scheduleRows';
import {
  EventBus,
  PROJECT_ROW_ADDED_OR_DELETED,
} from '@/utils/eventBus';
import ProductHeaders from '@/constants/productHeaders';

const SCHED_CMD_SUB = 'scheduleCommandSubscription';

export default {
  name: 'EditHistory',
  props: {
    showOnlyForRowId: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    dialog: false,
    // "Global" commands (add/update/del) made by everyone on the sched.
    scheduleCommands: [],
    // Filter states
    selectedUser: null,
    selectedType: null,
  }),
  computed: {
    ...mapState('ProjectDetailsTableSchedule', ['scheduleId']),
    ...mapState('ScheduleRows', ['viewInfo']),
    sortedEditHistory() {
      return this.scheduleCommands.toSorted((a, b) => {
        return Number(b.createdTime) - Number(a.createdTime);
      });
    },
    parsedEditHistory() {
      return this.sortedEditHistory.map((cmd) => {
        const clean = (val) => {
          let parsed;
          try {
            parsed = JSON.parse(val);
          } catch {
            parsed = val;
          }
          parsed = typeof parsed === 'object' && parsed !== null
            ? JSON.stringify(parsed)
            : (parsed ?? '');
          // eslint-disable-next-line
          return parsed.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
        };

        return {
          ...cmd,
          prevValue: clean(cmd.prevValue),
          value: clean(cmd.value),
        };
      });
    },
    filteredEditHistory() {
      let filtered = this.parsedEditHistory;
      if (this.showOnlyForRowId) {
        filtered = filtered.filter((a) => (a.rowId === this.showOnlyForRowId));
      } else if (this.selectedType) {
        filtered = filtered.filter((a) => this.getTypeForAction(a) === this.selectedType);
      }
      if (this.selectedUser) {
        filtered = filtered.filter((a) => (a.creatorName || 'Someone') === this.selectedUser);
      }
      return filtered;
    },
    currRowType() {
      if (!this.showOnlyForRowId) return '';
      const t = '<No Type>';
      const r = this.viewInfo?.find((r) => r.id === this.showOnlyForRowId);
      return r?.[ProductHeaders.TYPE_DESIGNATION] ?? t;
    },
    userOptions() {
      const users = new Set();
      for (const action of this.parsedEditHistory) {
        const userName = action.creatorName || 'Someone';
        users.add(userName);
      }
      return Array.from(users).sort();
    },
    typeOptions() {
      const types = new Set();
      for (const action of this.parsedEditHistory) {
        types.add(this.getTypeForAction(action));
      }
      return Array.from(types).sort();
    },
    hasActiveFilters() {
      return this.selectedUser || this.selectedType;
    },
  },
  watch: {
    scheduleId: {
      handler(val, oldVal) {
        try {
          if (oldVal) this.removeScheduleCommandSubscription(oldVal);
          if (val) {
            this.startScheduleCommandSubscription(val);
            if (val !== oldVal) {
              this.fetchScheduleCommands();
            }
          }
        } catch (e) {
          this.handleError(e);
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.fetchScheduleCommands();
    EventBus.$on(PROJECT_ROW_ADDED_OR_DELETED, (sid) => {
      if (this.scheduleId && this.scheduleId !== sid) return;
      this.fetchScheduleCommands();
    });
  },
  beforeDestroy() {
    this.removeScheduleCommandSubscription(this.scheduleId);
    EventBus.$off(PROJECT_ROW_ADDED_OR_DELETED);
  },
  methods: {
    ...mapActions(['handleError']),
    ...mapActions('ManageSubscriptions', ['setSubscription', 'removeSubscriptions']),
    /**
     * APIs Fetching/Subs
     */
    async fetchScheduleCommands() {
      if (!this.scheduleId) return;

      const res = await ScheduleAPI.getEditHistory({
        scheduleId: this.scheduleId,
      });
      const { response } = res.data ?? {
      };
      this.scheduleCommands = response ?? [];
    },
    async startScheduleCommandSubscription(schedId) {
      const sub = await subscribeForScheduleCommands({
        scheduleId: schedId,
      }, () => {
        this.fetchScheduleCommands();
      });
      this.setSubscription(SCHED_CMD_SUB + schedId, sub);
    },
    async removeScheduleCommandSubscription(schedId) {
      this.removeSubscriptions(SCHED_CMD_SUB + schedId);
    },

    /**
     * Filters
     */
    clearAllFilters() {
      this.selectedUser = null;
      this.selectedType = null;
    },

    /**
     * Formatting Data
     */
    getTypeForAction(action) {
      const t = '-';
      const existingRow = this.viewInfo?.find((r) => r.id === action?.rowId);
      if (existingRow) {
        return existingRow[ProductHeaders.TYPE_DESIGNATION] ?? t;
      } else {
        return action?.rowType ?? t;
      }
    },
    getTruncatedType(action) {
      const MAX_LENGTH = 12;
      let t = this.getTypeForAction(action);
      return t.length > MAX_LENGTH ? t.substring(0, MAX_LENGTH) + '...' : t;
    },
    getTruncatedUsername(action) {
      const MAX_LENGTH = 17;
      const name = action?.creatorName ?? 'Someone';
      return name.length > MAX_LENGTH ? name.substring(0, MAX_LENGTH) + '...' : name;
    },
    getActionCommand(action) {
      switch (action?.commandType) {
      case 'add':
        return 'added';
      case 'update':
        return 'updated';
      case 'delete':
        return 'deleted';
      default:
        return '-';
      }
    },
    getActionField(action) {
      if (this.isAddOrDeleteAction(action)) return ' a row';
      return action.column || '';
    },
    formatActionVal(action, prev) {
      const val = prev ? action.prevValue : action.value;
      if (!val) {
        return '<Empty>';
      }
      // TODO: Refactor - format ALL fields and more reliable column names.
      const actionCol = action.column ?? '';
      const HIDE_VAL_COLS = ['image', 'line drawing'];
      if (HIDE_VAL_COLS.includes(actionCol.toLowerCase())) {
        return '';
      }
      return val;
    },
    getTruncatedActionVal(action, prev) {
      if (this.isAddOrDeleteAction(action)) return '';

      const val = this.formatActionVal(action, prev);
      const str = String(val);
      const MAX_LEN = 30;
      return str.length > MAX_LEN
        ? str.substring(0, MAX_LEN) + '...'
        : str;
    },
    formatRelativeTimestamp(ts) {
      return this.$moment(Number(ts)).fromNow();
    },
    formatExactTimestamp(ts) {
      return this.$moment(Number(ts)).format('YYYY-MM-DD HH:mm:ss');
    },
    isAddOrDeleteAction(action) {
      return ['add', 'delete'].includes(action?.commandType);
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .v-card__title {
  display: flex !important;
  justify-content: space-between;
}

.edit-history-wrapper {
  font-family: Poppins;
  border: 1px solid var(--v-darkGrey-base);
  background: white;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.edit-history-row {
  padding: 0.5rem 0 1rem 0.5rem;
  display: grid;
  place-items: center;
  grid-template-columns: 4rem 8rem 4rem 10rem 1fr 1fr 8rem 0.01rem;
  gap: 1rem;
  font-size: 0.875rem;
  border-bottom: 1px solid var(--v-darkGrey-base);
  text-align: center;

  &__type {
    color: var(--v-green-base);
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
  }

  &:last-child {
    border-bottom: none;
  }

  &__timestamp {
    text-align: right;
  }

  ::v-deep .vlist-item {
    &::after {
      display: none;
    }
  }
}
</style>